import type { ProductQuestionGroupQuestion } from "../../client/generated";
import { ProductQuestionInput } from "./ProductQuestionInput";
import type { Control, FieldErrors, ControllerRenderProps, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toOriginalField, toSafeField } from "../../utils/questionnaireHelpers";

type QuestionRendererProps = {
  question: ProductQuestionGroupQuestion;
  index: number;
  control: Control<FieldValues>;
  errors: FieldErrors;
  formRef: React.RefObject<HTMLFormElement>;
};

/**
 * Shared component for rendering a single product question with validation
 */
export function QuestionRenderer({ question, index, control, errors, formRef }: QuestionRendererProps) {
  const { t } = useTranslation();
  const guid = question.product_question_group_question_guid;

  return (
    <div key={guid} data-id={guid} className="space-y-2">
      {/* provide type info for building correct payload */}
      <input type="hidden" name={`type-${guid}`} value={question.question_type} />
      <Controller
        control={control}
        name={toSafeField(guid)}
        render={({ field }: { field: ControllerRenderProps<FieldValues, string> }) => (
          <div>
            <ProductQuestionInput
              question={{ ...question, question_text: `${index + 1}. ${question.question_text}` }}
              value={(field.value as string) ?? ""}
              onChange={(val) => {
                field.onChange(val);
                // Also update the form field name for form submission
                const form = formRef.current;
                if (form) {
                  const hiddenInput = form.querySelector(`input[name="${toOriginalField(guid)}"]`) as HTMLInputElement;
                  if (hiddenInput) {
                    hiddenInput.value = val;
                  }
                }
              }}
            />
            {/* Hidden input for form submission */}
            <input type="hidden" name={toOriginalField(guid)} value={(field.value as string) ?? ""} />
            {errors?.[toSafeField(guid)] ? <p className="mt-1 text-sm text-red-600">{t("Bitte ausfüllen.")}</p> : null}
          </div>
        )}
      />
    </div>
  );
}
